import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { motion, AnimatePresence } from 'framer-motion'
import { AlertTriangle, Send, CheckCircle, MessageCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useAuthStore } from '@/stores/authStore'
import api from '@/api/client'
import { toast } from '@/components/ui/toaster'
import { formatDistanceToNow } from '@/lib/utils'

const urgencyColors: Record<string, string> = {
  low: 'bg-blue-500/10 text-blue-500',
  medium: 'bg-yellow-500/10 text-yellow-500',
  high: 'bg-orange-500/10 text-orange-500',
  critical: 'bg-red-500/10 text-red-500',
}

export default function SOSSignals() {
  const { t } = useTranslation()
  const { isAuthenticated } = useAuthStore()
  const queryClient = useQueryClient()
  const [showForm, setShowForm] = useState(false)
  const [content, setContent] = useState('')
  const [urgency, setUrgency] = useState('medium')
  const [category, setCategory] = useState('emotional')
  const [isAnonymous, setIsAnonymous] = useState(true)
  const [respondingTo, setRespondingTo] = useState<string | null>(null)
  const [responseMsg, setResponseMsg] = useState('')

  const categoryLabels: Record<string, string> = {
    emotional: t('sos.category.emotional'),
    loneliness: t('sos.category.loneliness'),
    crisis: t('sos.category.crisis'),
    support_needed: t('sos.category.supportNeeded'),
    other: t('sos.category.other'),
  }

  const { data: activeSignals } = useQuery({
    queryKey: ['sos-active'],
    queryFn: () => api.get('/sos/active').then(r => r.data),
    refetchInterval: 15000
  })

  const createMutation = useMutation({
    mutationFn: () => api.post('/sos', { content, urgency, category, isAnonymous }),
    onSuccess: () => {
      setShowForm(false)
      setContent('')
      setUrgency('medium')
      setCategory('emotional')
      queryClient.invalidateQueries({ queryKey: ['sos-active'] })
      toast({ title: t('sos.signalSent') })
    },
    onError: (err: any) => toast({ title: err.response?.data?.error || t('sos.error'), variant: 'destructive' })
  })

  const respondMutation = useMutation({
    mutationFn: (id: string) => api.post(`/sos/${id}/respond`, { message: responseMsg }),
    onSuccess: () => {
      setRespondingTo(null)
      setResponseMsg('')
      queryClient.invalidateQueries({ queryKey: ['sos-active'] })
      toast({ title: t('sos.responseSent') })
    },
    onError: (err: any) => toast({ title: err.response?.data?.error || t('common.error'), variant: 'destructive' })
  })

  const resolveMutation = useMutation({
    mutationFn: (id: string) => api.put(`/sos/${id}/resolve`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sos-active'] })
      toast({ title: t('sos.signalResolved') })
    },
    onError: (err: any) => toast({ title: err.response?.data?.error || t('common.error'), variant: 'destructive' })
  })

  return (
    <div className="max-w-2xl mx-auto px-4 py-6 relative">
      {/* Safe Exit */}
      <button
        onClick={() => window.location.href = 'https://tr.weather.com'}
        className="fixed top-20 right-4 z-40 px-3 py-1.5 rounded-full bg-muted text-xs font-medium border border-border hover:bg-secondary transition-colors shadow-sm"
      >
        {t('common.safeExit')}
      </button>

      <div className="text-center mb-8">
        <div className="w-14 h-14 rounded-2xl bg-red-500/10 flex items-center justify-center mx-auto mb-4">
          <AlertTriangle className="w-7 h-7 text-red-500" />
        </div>
        <h1 className="font-display text-2xl font-bold">{t('sos.title')}</h1>
        <p className="text-muted-foreground mt-2">{t('sos.subtitle')}</p>
      </div>

      {isAuthenticated && (
        <Button onClick={() => setShowForm(!showForm)} variant="destructive" className="w-full mb-6 rounded-xl">
          <AlertTriangle className="w-4 h-4 mr-2" /> {t('sos.sendSignal')}
        </Button>
      )}

      <AnimatePresence>
        {showForm && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className="mb-6 p-5 rounded-2xl bg-card border border-destructive/30 space-y-3">
            <textarea value={content} onChange={(e) => setContent(e.target.value)} placeholder={t('sos.contentPlaceholder')} className="w-full px-4 py-2 rounded-lg bg-secondary border border-border focus:outline-none focus:ring-2 focus:ring-destructive/50 resize-none min-h-[100px]" />
            <div className="grid grid-cols-2 gap-3">
              <select value={urgency} onChange={(e) => setUrgency(e.target.value)} className="px-3 py-2 rounded-lg bg-secondary border border-border text-sm">
                <option value="low">{t('sos.urgency.low')}</option>
                <option value="medium">{t('sos.urgency.medium')}</option>
                <option value="high">{t('sos.urgency.high')}</option>
                <option value="critical">{t('sos.urgency.critical')}</option>
              </select>
              <select value={category} onChange={(e) => setCategory(e.target.value)} className="px-3 py-2 rounded-lg bg-secondary border border-border text-sm">
                <option value="emotional">{t('sos.category.emotional')}</option>
                <option value="loneliness">{t('sos.category.loneliness')}</option>
                <option value="crisis">{t('sos.category.crisis')}</option>
                <option value="support_needed">{t('sos.category.supportNeeded')}</option>
                <option value="other">{t('sos.category.other')}</option>
              </select>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={isAnonymous} onChange={(e) => setIsAnonymous(e.target.checked)} className="rounded" />
              {t('sos.anonymous')}
            </label>
            <Button variant="destructive" onClick={() => createMutation.mutate()} disabled={createMutation.isPending || !content.trim()}>
              {createMutation.isPending ? t('sos.sending') : t('sos.send')}
            </Button>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="space-y-3">
        <h2 className="font-semibold mb-2">{t('sos.activeSignals')}</h2>
        {activeSignals?.length === 0 && (
          <div className="text-center py-8 text-muted-foreground text-sm">{t('sos.noSignals')}</div>
        )}
        {activeSignals?.map((signal: any) => (
          <motion.div key={signal._id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="p-4 rounded-xl bg-card border border-border">
            <div className="flex items-start justify-between mb-2">
              <div className="flex items-center gap-2">
                <span className={`text-xs px-2 py-0.5 rounded-full ${urgencyColors[signal.urgency]}`}>{t(`sos.${signal.urgency}`)}</span>
                <span className="text-xs text-muted-foreground">{categoryLabels[signal.category]}</span>
              </div>
              <span className="text-xs text-muted-foreground">{formatDistanceToNow(signal.createdAt)}</span>
            </div>
            <p className="text-sm mb-3">{signal.content}</p>

            {signal.responders?.length > 0 && (
              <div className="mb-3 space-y-1">
                <p className="text-xs text-muted-foreground font-medium">{t('sos.responses')}</p>
                {signal.responders.map((r: any, i: number) => (
                  <div key={i} className="text-xs bg-secondary/50 p-2 rounded-lg">
                    <span className="font-medium">{r.username}:</span> {r.message}
                  </div>
                ))}
              </div>
            )}

            {respondingTo === signal._id ? (
              <div className="flex gap-2">
                <input
                  value={responseMsg}
                  onChange={(e) => setResponseMsg(e.target.value)}
                  placeholder={t('sos.responsePlaceholder')}
                  className="flex-1 px-3 py-2 rounded-lg bg-secondary border border-border focus:outline-none focus:ring-2 focus:ring-primary/50 text-sm"
                />
                <Button size="sm" onClick={() => respondMutation.mutate(signal._id)} disabled={!responseMsg.trim()}>
                  <Send className="w-3 h-3" />
                </Button>
              </div>
            ) : (
              <div className="flex gap-2">
                <button onClick={() => setRespondingTo(signal._id)} className="text-xs px-3 py-1.5 rounded-lg bg-primary/10 text-primary hover:bg-primary/20 transition-colors">
                  <MessageCircle className="w-3 h-3 inline mr-1" /> {t('sos.respond')}
                </button>
                <button onClick={() => resolveMutation.mutate(signal._id)} className="text-xs px-3 py-1.5 rounded-lg bg-green-500/10 text-green-500 hover:bg-green-500/20 transition-colors">
                  <CheckCircle className="w-3 h-3 inline mr-1" /> {t('sos.resolve')}
                </button>
              </div>
            )}
          </motion.div>
        ))}
      </div>
    </div>
  )
}
